/* BEATSEEK — Featured Emote of the Week slide-out (desktop only ≥1100px).
 * - Tucked behind the Wall modal on the left edge, slides out from behind it
 * - Right-edge handle with chevron triangle + vertical title when collapsed
 * - Default expanded on first visit; persists pref in localStorage
 * - Top 3 stacked vertically, gold/silver/bronze ranks
 * - Tile click → actions slide DOWN inside the tile (Download/Remix/Save to Library)
 * - Subtle pulse on the handle when a NEW emote enters top 3 (cleared on open)
 *
 * Backend: GET /api/emote-lab/gallery?limit=3, POST /api/emote-lab/copy
 */
import React, { useEffect, useState, useRef } from 'react';
import { Trophy, Download, Wand2, BookmarkPlus, ChevronRight, ChevronLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_OPEN = 'beatseek_featured_emote_panel_open';
const STORAGE_LAST_TOP3 = 'beatseek_featured_emote_last_top3';

const EmoteOfTheWeek = ({ currentUser }) => {
  const [emotes, setEmotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null); // tile whose inline actions are open
  const [toast, setToast] = useState(null);
  const [viewport, setViewport] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [pulse, setPulse] = useState(false);
  const [emoteIdx, setEmoteIdx] = useState(0);
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(STORAGE_OPEN);
    if (stored === null) return false; // default COLLAPSED — discoverable via Wall card link
    return stored === '1';
  });
  const containerRef = useRef(null);

  // Persist toggle pref + clear pulse on open
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_OPEN, open ? '1' : '0');
    }
    if (open) setPulse(false);
  }, [open]);

  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Click-outside collapses any open tile actions
  useEffect(() => {
    if (!activeId) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setActiveId(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [activeId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/emote-lab/gallery?limit=3`);
        if (r.ok) {
          const d = await r.json();
          const list = d.emotes || [];
          if (mounted) setEmotes(list);

          // Detect a new entry in top 3 vs the last seen snapshot → pulse the handle
          try {
            const ids = list.map(x => x.id);
            const last = JSON.parse(localStorage.getItem(STORAGE_LAST_TOP3) || '[]');
            const hasNew = ids.some(id => !last.includes(id));
            if (hasNew && last.length > 0) setPulse(true);
            localStorage.setItem(STORAGE_LAST_TOP3, JSON.stringify(ids));
          } catch (_e) { /* ignore */ }
        }
      } catch (_e) { /* ignore */ }
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  const handleDownload = (emote) => {
    const a = document.createElement('a');
    a.href = `${API}/api/emote-lab/${emote.id}/file?d=1`;
    a.download = `${(emote.name || 'emote').replace(/[^a-z0-9_-]/gi, '_')}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    flash('Downloading…');
  };

  const handleRemix = (emote) => {
    window.dispatchEvent(new CustomEvent('beatseek:open-emote-lab', {
      detail: { prompt: emote.prompt || emote.name || '', style: emote.style || 'neon' }
    }));
    flash('Opening Emote Lab…');
  };

  const handleSaveToLibrary = async (emote) => {
    if (!currentUser?.id) { flash('Sign in first'); return; }
    try {
      const r = await fetch(`${API}/api/emote-lab/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: currentUser.id, source_emote_id: emote.id }),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Save failed');
      }
      flash('Saved to your library ✓');
    } catch (e) {
      flash(e.message || 'Save failed');
    }
  };

  // External toggle trigger — fired by the wall featured-emote card "View top 3" link.
  useEffect(() => {
    const handler = () => setOpen(o => !o);
    window.addEventListener('beatseek:open-featured-sidebar', handler);
    return () => window.removeEventListener('beatseek:open-featured-sidebar', handler);
  }, []);

  if (viewport < 1100) return null;

  const PANEL_WIDTH = 215;
  const HANDLE_WIDTH = 24;
  // Left-side rail (bottom slot) — anchored via `right` so the panel tucks behind the Wall's LEFT edge.
  const right = open ? `calc(50% + 336px)` : `calc(50% + 356px)`;

  return (
    <>
      {/* Inline keyframes for handle pulse + actions slide */}
      <style>{`
        @keyframes beatseekHandlePulse {
          0%, 100% { box-shadow: 0 8px 22px rgba(0,0,0,0.45), 0 0 0 0 rgba(34,211,238,0.0); }
          50% { box-shadow: 0 8px 22px rgba(0,0,0,0.45), 0 0 14px 4px rgba(34,211,238,0.55); }
        }
        @keyframes beatseekActionsSlide {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div
        ref={containerRef}
        data-testid="emote-of-the-week-sidebar"
        style={{
          position: 'fixed',
          bottom: '8vh',
          right,
          transition: 'right 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
          flexDirection: 'row-reverse',
          // NO explicit zIndex — relies on DOM order so the Wall modal paints over the
          // tucked-left portion of the panel. The popover children set their own zIndex.
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {/* Panel */}
        <div
          data-testid="emote-of-the-week-panel"
          style={{
            width: open ? `${PANEL_WIDTH}px` : '0px',
            opacity: open ? 1 : 0,
            overflow: 'hidden',
            maxHeight: open ? '32vh' : '0px',
            overflowY: 'hidden',
            background: 'linear-gradient(180deg, #0d0d14 0%, #07070b 100%)',
            borderTop: open ? '1px solid rgba(34,211,238,0.22)' : '1px solid transparent',
            borderRight: open ? '1px solid rgba(34,211,238,0.22)' : '1px solid transparent',
            borderBottom: open ? '1px solid rgba(34,211,238,0.22)' : '1px solid transparent',
            borderLeft: 'none',
            borderTopRightRadius: '14px',
            borderBottomRightRadius: '14px',
            padding: open ? '14px 28px 14px 14px' : '0',
            boxShadow: open ? '10px 0 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(192,38,211,0.12)' : 'none',
            transition: 'width 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease, padding 0.35s',
          }}
        >
          {/* Header — left-justified to avoid clipping behind the tucked right edge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', justifyContent: 'flex-start' }}>
            <div style={{
              fontSize: '10px', fontWeight: 800, letterSpacing: '0.16em',
              background: 'linear-gradient(135deg, #22d3ee, #e879f9)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textTransform: 'uppercase',
            }}>
              Featured Emotes
            </div>
            <Trophy size={14} style={{ color: '#fbbf24' }} />
          </div>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '12px', letterSpacing: '0.06em', textAlign: 'left' }}>
            Top 3 picks from the community
          </div>

          {loading ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: '#475569', fontSize: '11px' }}>
              Loading…
            </div>
          ) : emotes.length === 0 ? (
            <div style={{ padding: '14px', textAlign: 'center', fontSize: '11px', color: '#475569',
                          border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '10px' }}>
              <div style={{ marginBottom: '4px' }}>No public emotes yet.</div>
              <div>Be the first to publish one!</div>
            </div>
          ) : (
            <div>
              {(() => {
                const i = Math.min(emoteIdx, emotes.length - 1);
                const e = emotes[i];
                const isActive = activeId === e.id;
                return (
                  <div key={e.id} style={{ position: 'relative' }}>
                    {/* Rank number — clean bold gold/silver/bronze (no circle) */}
                    <div style={{
                      position: 'absolute', top: '-4px', left: '0px', zIndex: 2,
                      fontSize: '22px', fontWeight: 900,
                      lineHeight: 1, letterSpacing: '-0.02em',
                      color: i === 0 ? '#fbbf24'   /* gold */
                           : i === 1 ? '#cbd5e1'   /* silver */
                           : '#fb923c',            /* bronze */
                      textShadow: i === 0 ? '0 0 10px rgba(251,191,36,0.55), 0 2px 4px rgba(0,0,0,0.6)'
                                : i === 1 ? '0 0 10px rgba(203,213,225,0.45), 0 2px 4px rgba(0,0,0,0.6)'
                                : '0 0 10px rgba(251,146,60,0.45), 0 2px 4px rgba(0,0,0,0.6)',
                      pointerEvents: 'none',
                    }}>{i + 1}</div>

                    <button
                      data-testid={`featured-emote-${e.id}`}
                      onClick={() => setActiveId(prev => prev === e.id ? null : e.id)}
                      style={{
                        position: 'relative',
                        padding: '8px',
                        background: isActive
                          ? 'linear-gradient(135deg, rgba(34,211,238,0.22), rgba(232,121,249,0.22))'
                          : 'rgba(255,255,255,0.03)',
                        border: isActive
                          ? '1px solid #22d3ee'
                          : '1px solid rgba(255,255,255,0.07)',
                        borderRadius: '10px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '10px',
                        transition: 'all 0.18s',
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{
                        width: '50px', height: '50px', flexShrink: 0,
                        borderRadius: '8px', overflow: 'hidden',
                        background: '#0a0a14', border: '1px solid rgba(34,211,238,0.14)',
                      }}>
                        <img
                          src={`${API}/api/emote-lab/${e.id}/file`}
                          alt={e.name}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#fff',
                                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {e.name}
                        </div>
                        <div style={{ fontSize: '9px', color: '#64748b', marginTop: '2px',
                                      textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                          {e.style}
                        </div>
                        <div style={{ fontSize: '9px', color: '#22d3ee', marginTop: '2px', fontWeight: 700 }}>
                          ⬇ {e.downloads || 0}
                        </div>
                      </div>
                    </button>

                    {/* Inline actions (slide DOWN beneath the clicked tile) */}
                    {isActive && (
                      <div
                        data-testid="featured-emote-actions"
                        style={{
                          marginTop: '6px',
                          padding: '8px',
                          background: 'rgba(8,8,15,0.85)',
                          border: '1px solid rgba(34,211,238,0.3)',
                          borderRadius: '10px',
                          display: 'flex', flexDirection: 'column', gap: '6px',
                          animation: 'beatseekActionsSlide 0.2s ease',
                        }}
                      >
                        <button data-testid="featured-emote-download" onClick={() => handleDownload(e)} style={actionBtn('#22d3ee')}>
                          <Download size={11} /> Download PNG
                        </button>
                        <button data-testid="featured-emote-remix" onClick={() => handleRemix(e)} style={actionBtn('#e879f9')}>
                          <Wand2 size={11} /> Remix in Lab
                        </button>
                        <button data-testid="featured-emote-save-library" onClick={() => handleSaveToLibrary(e)} style={actionBtn('#fbbf24')}>
                          <BookmarkPlus size={11} /> Save to Library
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
              {emotes.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '9px' }}>
                  <button
                    data-testid="emote-pager-prev"
                    onClick={() => { setActiveId(null); setEmoteIdx(i => (i - 1 + emotes.length) % emotes.length); }}
                    style={emotePagerBtn}
                  >
                    <ChevronLeft size={13} />
                  </button>
                  <span data-testid="emote-pager-counter" style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.08em' }}>
                    {Math.min(emoteIdx, emotes.length - 1) + 1} / {emotes.length}
                  </span>
                  <button
                    data-testid="emote-pager-next"
                    onClick={() => { setActiveId(null); setEmoteIdx(i => (i + 1) % emotes.length); }}
                    style={emotePagerBtn}
                  >
                    <ChevronRight size={13} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Toggle handle */}
        <button
          data-testid="emote-of-the-week-toggle"
          onClick={() => { setOpen(o => !o); setActiveId(null); }}
          title={open ? 'Hide featured emotes' : 'Show featured emotes'}
          style={{
            width: `${HANDLE_WIDTH}px`,
            background: 'linear-gradient(180deg, rgba(34,211,238,0.14), rgba(192,38,211,0.14))',
            border: '1px solid rgba(34,211,238,0.32)',
            borderTopLeftRadius: '12px',
            borderBottomLeftRadius: '12px',
            borderTopRightRadius: open ? '0' : '12px',
            borderBottomRightRadius: open ? '0' : '12px',
            color: '#22d3ee',
            cursor: 'pointer',
            padding: '14px 0',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
            minHeight: '120px',
            boxShadow: open ? '0 8px 22px rgba(0,0,0,0.45), 0 0 14px 3px rgba(34,211,238,0.4)' : '0 8px 22px rgba(0,0,0,0.45)',
            transition: 'transform 0.2s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s, border-color 0.2s',
            animation: (pulse && !open) ? 'beatseekHandlePulse 1.6s ease-in-out infinite' : 'none',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(-4px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.5), 0 0 20px 5px rgba(34,211,238,0.6)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = open ? '0 8px 22px rgba(0,0,0,0.45), 0 0 14px 3px rgba(34,211,238,0.4)' : '0 8px 22px rgba(0,0,0,0.45)'; }}
        >
          {open ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
          {!open && (
            <div style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: '9px', fontWeight: 800, letterSpacing: '0.18em',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #22d3ee, #e879f9)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Emotes
            </div>
          )}
          {!open && <Trophy size={12} style={{ color: '#fbbf24' }} />}
        </button>

        {/* Toast */}
        {toast && (
          <div
            data-testid="featured-emote-toast"
            style={{
              position: 'absolute', bottom: '-34px', left: '50%',
              transform: 'translateX(-50%)',
              background: '#0d0d14',
              border: '1px solid rgba(34,211,238,0.4)',
              borderRadius: '999px', padding: '6px 12px',
              fontSize: '10px', color: '#22d3ee', fontWeight: 700,
              whiteSpace: 'nowrap', letterSpacing: '0.05em',
              boxShadow: '0 8px 20px rgba(0,0,0,0.5)',
              zIndex: 100003,
            }}>
            {toast}
          </div>
        )}
      </div>
    </>
  );
};

const emotePagerBtn = {
  width: '22px', height: '22px', borderRadius: '50%',
  background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.35)',
  color: '#22d3ee', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 0, transition: 'all 0.15s',
};

const actionBtn = (color) => ({
  width: '100%', padding: '6px 10px',
  background: 'rgba(255,255,255,0.03)',
  border: `1px solid ${color}`,
  color, fontSize: '10px', fontWeight: 700,
  letterSpacing: '0.04em',
  borderRadius: '7px', cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: '6px',
  transition: 'all 0.15s',
});

export default EmoteOfTheWeek;
